
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "react-loading-skeleton/dist/skeleton.css";
import { toast, ToastContainer } from "react-toastify";
import PageHeading from "../../PageHeading";
import DynamicSelect from "../../DynamicSelect";
import { useTranslation } from "react-i18next";
import { FiEdit2, FiSave } from "react-icons/fi";
import RichTextEditor from "../../RichTextEditor";
import TableSkeleton from "../../TableSkeleton";

function sanitizeContent(html) {
  return html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const EditBlog = ({ isSearchable }) => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();

  const [formData, setFormData] = useState({
    metaTitle: "",
    metaDescription: "",
    title: "",
    slug: "",
    description: "",
    content: "",
    category: { id: "", name: "" },
    image: null,
  });

  const [translations, setTranslations] = useState({
    en: {
      metaTitle: "",
      metaDescription: "",
      title: "",
      slug: "",
      description: "",
      content: "",
      category: { id: "", name: "" },
    },
    bn: {
      metaTitle: "",
      metaDescription: "",
      title: "",
      slug: "",
      description: "",
      content: "",
      category: { id: "", name: "" },
    },
  });

  const [language, setLanguage] = useState("en");
  const [isSlugEditable, setIsSlugEditable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [categoriesList, setCategoriesList] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await fetch("https://api.muktihospital.com/api/category", {
          headers: {
            "x-api-key":
              "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch categories");
        }
        const data = await response.json();
        setCategoriesList(data);
      } catch (error) {
        console.error("❌ Error fetching categories:", error);
        setError("Failed to load categories.");
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Fetch blog data
  useEffect(() => {
    const fetchBlog = async () => {
      setFetching(true);
      try {
        const res = await fetch(`https://api.muktihospital.com/api/blogs/${id}`, {
          headers: {
            "x-api-key":
              "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
          },
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || "Failed to fetch blog");
        }
        setTranslations(data.translations || { en: {}, bn: {} });
        const langData = data.translations?.[language] || {};
        let selectedCategory = langData.category || { id: "", name: "" };

        // Synchronize category name with the current language
        const categoryFromList = categoriesList.find((cat) => cat.id === selectedCategory.id);
        if (categoryFromList) {
          selectedCategory.name =
            categoryFromList.translations?.[language]?.name ||
            categoryFromList.translations?.en?.name ||
            "Unnamed Category";
        }

        setFormData({
          metaTitle: langData.metaTitle || "",
          metaDescription: langData.metaDescription || "",
          title: langData.title || "",
          slug: data.translations?.en?.slug || "",
          description: langData.description || "",
          content: langData.content || "",
          category: selectedCategory,
          image: null,
        });

        if (data.image) {
          setPreviewImage(`https://api.muktihospital.com/uploads/${data.image}`);
        }
      } catch (err) {
        console.error("❌ Error fetching blog:", err);
        setError(err.message);
      } finally {
        setFetching(false);
      }
    };

    fetchBlog();
  }, [id, language, categoriesList]);

  // Auto-generate slug
  useEffect(() => {
    if (formData.title && !formData.slug) {
      const generatedSlug = formData.title
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^\w\-]+/g, "");
      setFormData((prev) => ({ ...prev, slug: generatedSlug }));
    }
  }, [formData.title, formData.slug]);

  // Map categories to options
  const categoryOptions = categoriesList.map((category) => {
    const categoryName =
      category.translations?.[i18n.language]?.name ||
      category.translations?.en?.name ||
      "Unnamed Category";
    return {
      value: category.id,
      label: categoryName,
    };
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({ ...prev, image: file }));
    if (file) {
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      category: {
        id: selectedOption.value,
        name: selectedOption.label,
      },
    }));
  };

  const handleContentChange = (newContent) => {
    setFormData((prev) => ({ ...prev, content: newContent }));
  };

  const handleSlugEditToggle = () => {
    setIsSlugEditable((prev) => !prev);
  };

  const handleSlugChange = (e) => {
    setFormData((prev) => ({ ...prev, slug: e.target.value }));
  };

  const languageOptions = [
    { value: "en", label: "English" },
    { value: "bn", label: "Bangla" },
  ];

  // Handle language switch
  const handleLanguageChange = (selectedOption) => {
    const selectedLang = selectedOption.value;
    setLanguage(selectedLang);
    i18n.changeLanguage(selectedLang);

    let langData = translations[selectedLang] || {};
    let selectedCategory = langData.category || { id: "", name: "" };

    // Synchronize category name with the selected language
    const categoryFromList = categoriesList.find((cat) => cat.id === selectedCategory.id);
    if (categoryFromList) {
      selectedCategory.name =
        categoryFromList.translations?.[selectedLang]?.name ||
        categoryFromList.translations?.en?.name ||
        "Unnamed Category";
    } else if (selectedLang === "bn" && !selectedCategory.id) {
      // Fallback to English category if no category is set for Bangla
      selectedCategory = translations.en?.category || { id: "", name: "" };
      const englishCategory = categoriesList.find((cat) => cat.id === selectedCategory.id);
      if (englishCategory) {
        selectedCategory.name =
          englishCategory.translations?.[selectedLang]?.name ||
          englishCategory.translations?.en?.name ||
          "Unnamed Category";
      }
    }

    const englishSlug = translations.en?.slug || "";

    setFormData({
      metaTitle: langData.metaTitle || "",
      metaDescription: langData.metaDescription || "",
      title: langData.title || "",
      slug: englishSlug,
      description: langData.description || "",
      content: langData.content || "",
      category: selectedCategory,
      image: null,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    setError(null);

    try {
      const updatedTranslations = {
        ...translations,
        [language]: {
          ...translations[language],
          metaTitle: formData.metaTitle,
          metaDescription: formData.metaDescription,
          title: formData.title,
          slug: formData.slug,
          description: formData.description,
          content: sanitizeContent(formData.content),
          category: formData.category,
        },
      };

      const data = new FormData();
      data.append("translations", JSON.stringify(updatedTranslations));
      if (formData.image) {
        data.append("image", formData.image);
      }

      const response = await fetch(`https://api.muktihospital.com/api/blogs/edit/${id}`, {
        method: "PUT",
        headers: {
          "x-api-key":
            "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
        },
        body: data,
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || "Failed to update blog");
      }

      toast.success(result.message || "Blog updated successfully!");
    } catch (err) {
      console.error("❌ Error updating blog:", err);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

// EditBlog.js
if (fetching || loadingCategories) {
  return (
    <div className="container mx-auto p-6">
      <TableSkeleton rows={6} columns={[1, 2]} />
    </div>
  );
}

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Manage Blogs"},
    { label: "Edit Blog" },
  ];

  return (
    <div>
      <ToastContainer />
      <PageHeading title="Edit Blog" breadcrumbs={breadcrumbs} />
      {message && <p className="mb-4 text-green-600">{message}</p>}
      {error && <p className="mb-4 text-red-600">{error}</p>}
      <div className="mb-4 flex justify-end">
        <DynamicSelect
          options={languageOptions}
          onChange={handleLanguageChange}
          value={languageOptions.find((option) => option.value === language)}
          isSearchable={isSearchable}
        />
      </div>
      <div>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <div>
              <div className="bg-white shadow-sm rounded-lg pb-2">
                <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                  <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                    Basic Information
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5">
                  <div>
                    <label className="label">Title</label>
                    <input
                      type="text"
                      name="title"
                      placeholder="Title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="input-field"
                      required
                    />
                  </div>
                  <div>
                    <label className="label">Slug (Editable)</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        name="slug"
                        value={formData.slug}
                        onChange={handleInputChange}
                        className="input-field flex-1"
                        readOnly={!isSlugEditable}
                      />
                      <button
                        type="button"
                        onClick={handleSlugEditToggle}
                        className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition"
                        title={isSlugEditable ? "Save Slug" : "Edit Slug"}
                      >
                        {isSlugEditable ? <FiSave className="w-5 h-5" /> : <FiEdit2 className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="label">Description</label>
                    <textarea
                      name="description"
                      placeholder="Short Description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="input-field"
                      required
                    />
                  </div>
                  <div>
                    <label className="label">Category</label>
                    <DynamicSelect
                      name="category"
                      value={{
                        value: formData.category.id,
                        label: formData.category.name,
                      }}
                      onChange={handleChange}
                      options={categoryOptions}
                      className="w-full p-3 border border-gray-300 rounded-md"
                      required
                    />
                  </div>
                </div>
                <div className="px-5 mb-5">
                  <label className="label">Content</label>
                  <RichTextEditor value={formData.content} onChange={handleContentChange} />
                </div>
              </div>
            </div>
            <div>
              <div className="bg-white shadow-sm rounded-lg">
                <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                  <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                    SEO Configuration
                  </h2>
                </div>
                <div className="p-5 grid grid-cols-2 gap-6">
                  <div>
                    <label className="label">Meta Title</label>
                    <input
                      type="text"
                      name="metaTitle"
                      className="input-field"
                      placeholder="Enter Meta Title"
                      value={formData.metaTitle}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label className="label">Meta Description</label>
                    <input
                      type="text"
                      name="metaDescription"
                      className="input-field"
                      placeholder="Enter Meta Description"
                      value={formData.metaDescription}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
              <div className="bg-white shadow-sm rounded-lg p-6 my-2">
                {previewImage && (
                  <img
                    src={previewImage}
                    alt="Preview"
                    className="mt-3 w-32 h-32 object-cover rounded-md"
                  />
                )}
                <input
                  type="file"
                  name="image"
                  onChange={handleImageChange}
                  className="w-full p-3 mt-3 border border-gray-300 rounded-md"
                  accept="image/jpeg, image/png, image/gif, image/webp"
                />
                <p className="text-sm text-gray-600 mt-1">
                  Max file size: {MAX_FILE_SIZE / 1024 / 1024}MB. Allowed types: JPEG, PNG, GIF, WEBP
                </p>
              </div>
              <div className="mt-6 flex gap-4 justify-end">
                <button
                  type="submit"
                  className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
                  disabled={loading}
                >
                  {loading ? "Updating..." : "Update Blog"}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditBlog;


